import { type InstantPayoutFeeData } from '@/components/payouts/types/instantPayoutsContext';
import { EventAvailableBalanceResponse } from '@/modules/Services/v3/types';
import { FeedbackScreenContent, PayoutBackendData } from '@/types';
import { getExternalURL } from '@/utils';

// Default fallback values for payout overview data
export const DEFAULT_AMOUNT_SYMBOL = '$0.00';

export const DEFAULT_CURRENCY_CODE = 'USD';

export const BALANCE_API_DEFAULT_TIMEZONE = 'America/Los_Angeles';

// Default available balance for instant payouts
export const DEFAULT_AVAILABLE_BALANCE = {
  display: '$0.00',
  value: 0,
} as const;

// Constants for React Query stale time (2 minutes as per data layer patterns)
export const STALE_TIME = 2 * 60 * 1000;

// Default reserve percentage for Instant Payouts (from eb-ui)
export const DEFAULT_RESERVE_PERCENTAGE = 20;

// Payout balance processing constants
export const PERCENT_TO_DECIMAL_DIVIDER = 100;

// Default payout overview structure for fallback
export const DEFAULT_PAYOUT_OVERVIEW = {
  // Display strings (kept for backwards compatibility)
  payoutTotal: DEFAULT_AMOUNT_SYMBOL,
  remainingPayout: DEFAULT_AMOUNT_SYMBOL,
  adjustmentsTotal: DEFAULT_AMOUNT_SYMBOL,

  // Raw values in minor currency units (cents) for formatting
  payoutTotalValue: 0,
  payoutTotalCurrency: DEFAULT_CURRENCY_CODE,
  remainingValue: 0,
  remainingCurrency: DEFAULT_CURRENCY_CODE,
  adjustmentsValue: 0,
  adjustmentsCurrency: DEFAULT_CURRENCY_CODE,

  // Additional data
  recentPayout: undefined,
} as const;

// Default payout balance structure for fallback
export const DEFAULT_PAYOUT_BALANCE = {
  organizationId: '',
  eventId: '',
  payoutBalanceDetails: {
    breakdown: {
      pastPayouts: 0,
      netSales: 0,
      adjustments: 0,
    },
    latestTransactionTimestamp: '',
    currencyPrecision: 2,
    payoutExposure: 0,
    latestPayoutTimestamp: '',
    amount: 0,
    currencyCode: DEFAULT_CURRENCY_CODE,
  },
  settings: {
    threshold: 0,
    frequency: 'manual',
    isOnHold: false,
  },
} as const;

// Default payout data structure for fallback (BFF PayoutService response)
export const DEFAULT_PAYOUT_DATA = {
  totalAmount: {
    value: 0,
    currencyPrecision: 2,
  },
  recentPayout: null,
} as const;

// Payout-specific permissions required for accessing payout account features
export const PAYOUT_ACCOUNT_PERMISSIONS = [
  'event.billing.charges_credits:read',
  'event.billing.payouts_summary',
  'event.billing.payouts',
  'event.payments.checkout_settings',
  'organization.billing.plan:manage',
  'user.payments.payout_authnet_credentials',
  'user.payments.payout_instruments',
  'user.payments.settings',
  'user.payments.user_instruments',
  'user.workspace.finance',
] as const;

// Default API response structure for graceful fallbacks (matches EventAvailableBalanceResponse)
export const DEFAULT_AVAILABLE_BALANCE_RESPONSE: EventAvailableBalanceResponse = {
  organization_id: '',
  event_id: '',
  payout_balance_details: {
    breakdown: {
      royalties: 0,
      adjustments: 0,
      paid_out_totals: {
        past_payouts: 0,
        itemized_sales: 0,
        itemized_fees: 0,
        itemized_tax: 0,
        itemized_adjustments: 0,
      },
      eb_service_fee: 0,
      eb_taxes: 0,
      tax: 0,
      net_sales: 0,
      past_payouts: 0,
      organization_adjustments: 0,
      gross_sales: 0,
      event_adjustments: 0,
      eb_payment_fee: 0,
    },
    latest_transaction_timestamp: '',
    currency_precision: 2,
    payout_exposure: 0,
    deductions: {
      cap_overage: 0,
      reserve_amount: 0,
    },
    latest_payout_timestamp: '',
    amount: 0,
    currency_code: DEFAULT_CURRENCY_CODE,
  },
  settings: {
    is_early_payout_eligible: false,
    cap: 0,
    reserve_percent: 0,
    instant_payout_fee: {
      max: 0,
      percent: 0,
      min: 0,
    },
    frequency: '',
    is_on_hold: false,
  },
};

//Instant payouts related constants
export const INSTANT_PAYOUTS_DEFAULT_TRIGGER_TYPE = 'INSTANT';
export const INSTANT_PAYOUTS_DEFAULT_TYPE = 'INSTANT';

/**
 * Builds the manage account URL with the correct TLD for the current environment
 * Automatically localizes to the user's domain (.com, .co.uk, .com.au, etc.)
 *
 * @returns The localized manage account URL
 */
export const getManageAccountUrl = (): string => {
  return getExternalURL('/account-settings/payment-methods');
};

export const DEFAULT_FEEDBACK_SCREEN_CONTENT: FeedbackScreenContent = {
  screenFooter: '',
  screenTitle: '',
  screenIcon: null,
  showPayoutData: false,
};

export const DEFAULT_PAYOUT_BACKEND_DATA: PayoutBackendData = {
  payoutId: '',
  requestedAmount: 0,
  fee: 0,
  payoutAmount: 0,
};

export const DEFAULT_INSTANT_PAYOUT_FEE_DATA: InstantPayoutFeeData = {
  percent: '0%',
  min: '$0.00',
  max: '$0.00',
};
